import React, { useEffect, useRef, useState } from 'react'
import Loader from '~/prix/react/components/loader'
import { fromUnknownError } from '~/prix'
import mapboxgl from 'mapbox-gl'
import publicConfig from '~/utils/client/publicConfig'
import { MapContext } from './mapContext'
import { LatLngBoundsLiteral, LatLngLiteral } from '../map/mapPosition'
import { MapType } from '../map/mapContext'
import useEffectSkipFirst from '~/prix/react/hooks/effectSkipFirst'
import './customMapStyles.css'
import { BrowserName, useBrowser } from '../utils/hooks/useBrowser'

mapboxgl.accessToken = publicConfig().services.mapbox.accessToken

export type MapGeneralProps = {
  tilt?: number
  heading?: number
  mapType?: MapType
  children?: React.ReactNode
  is3D?: boolean
  projection?: mapboxgl.MapOptions['projection']
  onClick?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onContextMenu?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onMouseUp?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onMouseDown?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onMouseMove?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onMouseOut?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onMouseOver?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onDrag?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onDragEnd?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onDragStart?: (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => void
  onZoomChanged?: (newZoom: number | undefined) => void
  onZoomEnd?: (newZoom: number | undefined) => void
  onBoundsChanged?: (bounds: LatLngBoundsLiteral) => void
  transitionDuration?: number
}

export type MapProps = (
  | {
      bounds?: never
      center: LatLngLiteral
      zoom: number
    }
  | {
      bounds: LatLngBoundsLiteral
      center?: never
      zoom?: never
    }
) &
  MapGeneralProps

const mapTypeStyles = {
  roadmap: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  terrain: 'mapbox://styles/mapbox/outdoors-v12',
  hybrid: 'mapbox://styles/mapbox/satellite-streets-v12',
}

/**
 * Componente de mapa com várias propriedades configuráveis.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {Object} [props.bounds] - Limites do mapa.
 * @param {Object} [props.center] - Coordenadas do centro do mapa.
 * @param {number} [props.zoom] - Nível de zoom do mapa.
 * @param {number} [props.tilt=0] - Inclinação do mapa. Default: `0`.
 * @param {number} [props.heading=0] - Direção do mapa. Default: `0`.
 * @param {string} [props.mapType='roadmap'] - Tipo de mapa. Default: `'roadmap'`.
 * @param {boolean} [props.is3D=false] - Habilita visualização 3D. Default: `false`.
 * @param {string} [props.projection] - Projeção do mapa (ex: 'mercator', 'globe').
 * @param {React.ReactNode} [props.children] - Componentes filhos (camadas).
 * @param {function} [props.onClick] - Função chamada no evento de clique.
 * @param {function} [props.onContextMenu] - Função chamada no evento de clique com botão direito.
 * @param {function} [props.onMouseUp] - Função chamada no evento de soltar o clique.
 * @param {function} [props.onMouseDown] - Função chamada no evento de clicar.
 * @param {function} [props.onMouseMove] - Função chamada no evento de mover o mouse.
 * @param {function} [props.onMouseOut] - Função chamada no evento de sair do mapa.
 * @param {function} [props.onMouseOver] - Função chamada no evento de passar o mouse.
 * @param {function} [props.onDrag] - Função chamada no evento de arrastar.
 * @param {function} [props.onDragEnd] - Função chamada no evento de soltar o arraste.
 * @param {function} [props.onDragStart] - Função chamada no evento de iniciar o arraste.
 * @param {function} [props.onZoomChanged] - Função chamada no evento de mudança de estado do zoom.
 * @param {function} [props.onZoomEnd] - Função chamada no evento de término da mudança de estado do zoom.
 * @param {function} [props.onBoundsChanged] - Função chamada quando os limites do mapa mudam.
 */

export default function MapboxMap({
  bounds,
  center,
  zoom,
  mapType = 'roadmap',
  is3D = false,
  tilt = is3D ? 25 : 0,
  heading = 0,
  children,
  projection = 'globe',
  onClick,
  onContextMenu,
  onMouseUp,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onDrag,
  onDragEnd,
  onDragStart,
  onZoomChanged,
  onZoomEnd,
  onBoundsChanged,
  transitionDuration = 1000,
}: MapProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const isLoading = false
  const error: Error | null = null
  const [map, setMap] = useState<mapboxgl.Map | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [countInstantiate, setCountInstantiate] = useState<number>(0)
  const browser = useBrowser()

  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  useEffect(() => {
    if (!isMounted || !wrapperRef.current) return

    let map
    if (bounds) {
      const mapboxBounds: mapboxgl.LngLatBoundsLike | undefined = bounds
        ? [
            [bounds.west, bounds.south],
            [bounds.east, bounds.north],
          ]
        : undefined
      map = new mapboxgl.Map({
        container: wrapperRef.current,
        style: mapTypeStyles[mapType as keyof typeof mapTypeStyles],
        bounds: mapboxBounds,
        pitch: tilt,
        bearing: heading,
        antialias: true,
        language: 'pt-BR',
        projection: projection,
      })
    } else {
      map = new mapboxgl.Map({
        container: wrapperRef.current,
        style: mapTypeStyles[mapType as keyof typeof mapTypeStyles],
        center,
        zoom,
        pitch: tilt,
        bearing: heading,
        antialias: true,
        language: 'pt-BR',
        projection: projection,
      })
    }
    ;(
      wrapperRef.current as HTMLDivElement & {
        mapInstance: mapboxgl.Map
      }
    ).mapInstance = map

    map.on('idle', function () {
      map.resize()
    })

    setMap(map)
    const canvas = map.getCanvas()

    const handleContextLost = () => {
      setCountInstantiate(prevData => prevData + 1)
    }

    canvas.addEventListener('webglcontextlost', handleContextLost, false)
    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost)
      map.remove()
      setMap(null)
    }
  }, [isMounted, wrapperRef.current, countInstantiate])

  // Bounds
  useEffect(() => {
    if (!map || !bounds) {
      return
    }

    const mapboxBounds: mapboxgl.LngLatBoundsLike = [
      [bounds.west, bounds.south],
      [bounds.east, bounds.north],
    ]

    const camera = map.cameraForBounds(mapboxBounds, {
      padding: 10,
    })
    if (!camera) {
      return
    }

    map.flyTo({
      center: camera.center,
      zoom: camera.zoom,
      pitch: tilt,
      bearing: heading ?? camera.bearing,
      duration: transitionDuration,
    })
  }, [map, bounds, tilt, heading])

  // Center/zoom
  useEffect(() => {
    if (!map || bounds || !center || !zoom) {
      return
    }

    map.flyTo({
      center,
      zoom,
      pitch: tilt,
      bearing: heading,
      duration: transitionDuration,
    })
  }, [map, center, zoom, tilt, heading, bounds])

  useEffectSkipFirst(
    () => {
      map?.setStyle(mapTypeStyles[mapType as keyof typeof mapTypeStyles])
    },
    [map, mapType],
    () => !map,
  )

  useEffect(() => {
    if (!map || !wrapperRef.current) {
      return
    }
    setIsReady(false)

    const handleStyleLoaded = () => {
      setIsReady(true)
    }

    if (!map.isStyleLoaded() && browser.name === BrowserName.safari) {
      map.once('styledata', handleStyleLoaded)
    }

    map.on('style.load', handleStyleLoaded)
    return () => {
      setIsReady(false)
      if (map) {
        map.off('style.load', handleStyleLoaded)
      }
    }
  }, [map, mapType])

  const handleZoomChanged = () => {
    if (!map) return

    if (onZoomChanged) {
      onZoomChanged(map.getZoom())
    }
  }

  const handleZoomEnd = () => {
    if (!map) return

    if (onZoomEnd) {
      onZoomEnd(map.getZoom())
    }
  }

  const handleBoundsChanged = () => {
    if (!map || !onBoundsChanged) return

    const mapBounds = map.getBounds()
    const bounds: LatLngBoundsLiteral = {
      north: mapBounds?.getNorth() ?? 0,
      south: mapBounds?.getSouth() ?? 0,
      east: mapBounds?.getEast() ?? 0,
      west: mapBounds?.getWest() ?? 0,
    }
    onBoundsChanged(bounds)
  }

  useEffect(() => {
    if (!map) {
      return
    }

    const eventHandlers = [
      { type: 'click', handler: onClick },
      { type: 'contextmenu', handler: onContextMenu },
      { type: 'mouseup', handler: onMouseUp },
      { type: 'mousedown', handler: onMouseDown },
      { type: 'mousemove', handler: onMouseMove },
      { type: 'mouseout', handler: onMouseOut },
      { type: 'mouseover', handler: onMouseOver },
      { type: 'drag', handler: onDrag },
      { type: 'dragend', handler: onDragEnd },
      { type: 'dragstart', handler: onDragStart },
      { type: 'zoom', handler: handleZoomChanged },
      { type: 'zoomend', handler: handleZoomEnd },
      { type: 'moveend', handler: handleBoundsChanged },
    ]

    eventHandlers.forEach(({ type, handler }) => {
      if (handler) {
        map.on(type, handler)
      }
    })

    return () => {
      eventHandlers.forEach(({ type, handler }) => {
        if (handler) {
          map.off(type, handler)
        }
      })
    }
  }, [
    map,
    onClick,
    onContextMenu,
    onMouseUp,
    onMouseDown,
    onMouseMove,
    onMouseOut,
    onMouseOver,
    onDrag,
    onDragEnd,
    onDragStart,
    onZoomChanged,
    handleZoomEnd,
    onBoundsChanged,
  ])

  // 3D
  useEffect(() => {
    try {
      if (!map || !map.getStyle() || !isReady || !is3D) {
        return
      }
    } catch {
      return
    }

    const color = '#dedede'
    const extrusionHeight: number | null = 30
    const alpha = 0.6

    map.addLayer({
      id: '3d-buildings',
      source: 'composite',
      'source-layer': 'building',
      filter: ['==', 'extrude', 'true'],
      type: 'fill-extrusion',
      minzoom: 15,
      paint: {
        'fill-extrusion-color': color,
        'fill-extrusion-height': extrusionHeight
          ? extrusionHeight
          : ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
        'fill-extrusion-opacity': alpha,
        ...(extrusionHeight
          ? {
              'fill-extrusion-base': [
                'interpolate',
                ['linear'],
                ['zoom'],
                15,
                0,
                15.05,
                ['get', 'min_height'],
              ],
            }
          : {}),
      },
    })

    return () => {
      try {
        if (!map || !map.getStyle() || !map.getLayer('3d-buildings')) {
          return
        }
      } catch {
        return
      }
      map.removeLayer('3d-buildings')
    }
  }, [map, is3D, mapType, isReady])

  return (
    <MapContext.Provider value={{ map, is3D, mapType }}>
      <main style={{ height: '100%', width: '100%' }}>
        {isLoading || !wrapperRef.current ? (
          <Loader />
        ) : error ? (
          <div>{fromUnknownError(error).message}</div>
        ) : !map ? (
          <div>Mapa indisponível</div>
        ) : null}
        <div className='mapbox' ref={wrapperRef} style={{ height: '100%', width: '100%' }}></div>
        {isReady ? children : null}
      </main>
    </MapContext.Provider>
  )
}
